import { useTranslation } from 'react-i18next';

import { type ModalComponentProps } from '@/shared/components/Modals/modal-registry/modal-registry.types';
import { EmptyState } from '@/shared/components/feedback/EmptyState/EmptyState';
import { Button } from '@/shared/components/ui/Button/Button';
import { Checkbox } from '@/shared/components/ui/Checkbox/Checkbox';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/Dialog/Dialog';
import { Label } from '@/shared/components/ui/Label/Label';
import { Textarea } from '@/shared/components/ui/Textarea/Textarea';

import { useRequestChangesModalLogic } from './useRequestChangesModalLogic';

/**
 * Sending named steps back, with an instruction.
 *
 * The note is **mandatory** and the dialog enforces it, because that is the one
 * decision whose whole purpose is to tell somebody what to do differently:
 * "changes requested" with nothing to change is a dead end the member cannot
 * escape (`RequestChangesSchema`'s own words).
 *
 * When there is nothing left to ask for — every member-submittable step already
 * accepted or skipped — it says so rather than offering an empty checklist above
 * a button that answers 422.
 */
export function RequestChangesModal({ open, onCancel, modalData }: ModalComponentProps) {
  const { t } = useTranslation(['verification', 'common']);
  const view = useRequestChangesModalLogic(modalData, onCancel);

  if (view.caseId === null) {
    return null;
  }

  const member = view.member ?? t('verification:queue.missingAccount');

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onCancel();
      }}
    >
      <DialogContent size="md">
        <DialogHeader>
          <DialogTitle>{t('verification:requestChanges.title')}</DialogTitle>
          <DialogDescription>{t('verification:detail.subtitle')}</DialogDescription>
        </DialogHeader>

        <DialogBody>
          {view.available.length === 0 ? (
            <EmptyState
              title={t('verification:requestChanges.noSteps')}
              description={t('verification:requestChanges.noStepsHint')}
            />
          ) : (
            <>
              <p className="text-body text-foreground">
                {t('verification:requestChanges.lead', { member })}
              </p>

              <fieldset className="flex flex-col gap-sm">
                {/* A real `<fieldset>`/`<legend>`, so the group has an accessible
                    name of its own — a heading beside checkboxes names nothing. */}
                <legend className="mb-xs text-caption font-medium text-foreground">
                  {t('verification:requestChanges.stepsLabel')}
                </legend>

                {view.available.map((step) => (
                  <div key={step} className="flex items-center gap-sm">
                    <Checkbox
                      id={`verification-request-${step}`}
                      checked={view.selected.includes(step)}
                      onCheckedChange={() => {
                        view.toggleStep(step);
                      }}
                    />
                    <Label htmlFor={`verification-request-${step}`}>
                      {t(`verification:step.key.${step}`)}
                    </Label>
                  </div>
                ))}

                <p className="text-caption text-muted-foreground">
                  {t('verification:requestChanges.stepsHint')}
                </p>
              </fieldset>

              <div className="flex flex-col gap-xs">
                <Label htmlFor="verification-request-note">
                  {t('verification:requestChanges.noteLabel')}
                </Label>
                <Textarea
                  id="verification-request-note"
                  aria-describedby="verification-request-note-hint"
                  value={view.note}
                  maxLength={2000}
                  onChange={(event) => {
                    view.setNote(event.target.value);
                  }}
                />
                <p
                  id="verification-request-note-hint"
                  className="text-caption text-muted-foreground"
                >
                  {t('verification:requestChanges.noteHint')}
                </p>
              </div>
            </>
          )}

          {view.error === null ? null : (
            <p role="alert" className="text-caption text-destructive">
              {view.error}
            </p>
          )}
        </DialogBody>

        <DialogFooter>
          <Button variant="ghost" onClick={onCancel}>
            {t('common:action.cancel')}
          </Button>
          {view.available.length === 0 ? null : (
            <Button
              isLoading={view.isSending}
              disabled={!view.canSubmit}
              onClick={() => {
                void view.submit();
              }}
            >
              {t('verification:requestChanges.submit')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
